/**
 * Tenant Branding Overlay
 * ───────────────────────
 * White-label configuration for each customer tenant: subdomain, logo,
 * primary/accent brand colours, and display name overrides.
 *
 * In production this would be loaded from the customer's tenant record in
 * the database; here it lives as a static map for the UAT/demo build.
 *
 * Subdomain mapping
 * ─────────────────
 * The resolver in `tenantResolver.js` reads `window.location.hostname`,
 * extracts the leftmost label, and looks it up here. Examples:
 *
 *   acme.clearequal.com         → 'acme'
 *   bosch.clearequal.com        → 'bosch'
 *   uat.clearequal.com          → 'uat' (falls back to default tenant)
 *   localhost / *.vercel.app    → uses ?tenant= query param or default
 *
 * Brand colours
 * ─────────────
 * Each tenant exposes four CSS custom properties that override the global
 * tokens at runtime:
 *   --tenant-primary       Main brand colour (buttons, links, accents)
 *   --tenant-primary-hover Slightly darker variant for hover states
 *   --tenant-on-primary    Foreground colour on primary backgrounds
 *   --tenant-accent        Secondary brand colour for highlights
 *
 * Logo URLs are inline SVG data URIs so the build is fully self-contained
 * and works offline. In production, swap these for the customer's hosted
 * logo asset (PNG/SVG) on their CDN.
 */

// ─── Inline SVG logos (data URIs) ─────────────────────────────────────────
// These are placeholder logos showing the customer's name in their brand
// colour. In production, customer uploads their own logo at onboarding.

const makeLogoSvg = (text, color) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 48' width='200' height='48'>
    <rect width='200' height='48' fill='none'/>
    <text x='8' y='32' font-family='-apple-system,Segoe UI,Roboto,sans-serif' font-size='22' font-weight='700' fill='${color}' letter-spacing='-0.5'>${text}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const TENANT_BRANDING = {
  'clearequal-demo': {
    subdomain: null,
    displayName: 'ClearEqual',
    legalName: 'ClearEqual Ltd.',
    logoUrl: null,
    logoMonoUrl: null,
    favicon: 'CE',
    colors: {
      primary: '#0F172A',
      primaryHover: '#1E293B',
      onPrimary: '#FFFFFF',
      accent: '#F4A43A',
    },
    supportEmail: 'support@clearequal.com',
    supportPhone: null,
    headquartersCity: 'Brussels',
  },

  'global-consultancy-de': {
    subdomain: 'helix',
    displayName: 'Helix Advisory',
    legalName: 'Helix Advisory GmbH',
    logoUrl: makeLogoSvg('HELIX', '#0F4C75'),
    logoMonoUrl: makeLogoSvg('HELIX', '#1A1A1A'),
    favicon: '🔵',
    colors: {
      primary: '#0F4C75',
      primaryHover: '#0A3A5C',
      onPrimary: '#FFFFFF',
      accent: '#3282B8',
    },
    supportEmail: 'people@helix-advisory.com',
    supportPhone: '+49 30 1234 5678',
    headquartersCity: 'Berlin',
  },

  'consumer-goods-de': {
    subdomain: 'noravia',
    displayName: 'Noravia Consumer',
    legalName: 'Noravia Consumer Brands AG',
    logoUrl: makeLogoSvg('NORAVIA', '#C2185B'),
    logoMonoUrl: makeLogoSvg('NORAVIA', '#1A1A1A'),
    favicon: '🟣',
    colors: {
      primary: '#C2185B',
      primaryHover: '#9F1648',
      onPrimary: '#FFFFFF',
      accent: '#F06292',
    },
    supportEmail: 'compensation@noravia.de',
    supportPhone: '+49 89 9876 5432',
    headquartersCity: 'Munich',
  },

  'global-bank-es': {
    subdomain: 'avanza',
    displayName: 'Avanza Bank',
    legalName: 'Avanza Banco S.A.',
    logoUrl: makeLogoSvg('AVANZA', '#D32F2F'),
    logoMonoUrl: makeLogoSvg('AVANZA', '#1A1A1A'),
    favicon: '🔴',
    colors: {
      primary: '#D32F2F',
      primaryHover: '#A82323',
      onPrimary: '#FFFFFF',
      accent: '#FFA000',
    },
    supportEmail: 'rrhh@avanzabank.es',
    supportPhone: '+34 91 555 0100',
    headquartersCity: 'Madrid',
  },

  'global-manufacturer-ch': {
    subdomain: 'maschberg',
    displayName: 'Maschberg Industries',
    legalName: 'Maschberg Industries AG',
    logoUrl: makeLogoSvg('MASCHBERG', '#2E7D32'),
    logoMonoUrl: makeLogoSvg('MASCHBERG', '#1A1A1A'),
    favicon: '🟢',
    colors: {
      primary: '#2E7D32',
      primaryHover: '#236127',
      onPrimary: '#FFFFFF',
      accent: '#558B2F',
    },
    supportEmail: 'people@maschberg.ch',
    supportPhone: '+41 44 123 4567',
    headquartersCity: 'Zurich',
  },

  'global-security-uk': {
    subdomain: 'sentinel',
    displayName: 'Sentinel Defence',
    legalName: 'Sentinel Defence Holdings PLC',
    logoUrl: makeLogoSvg('SENTINEL', '#1565C0'),
    logoMonoUrl: makeLogoSvg('SENTINEL', '#1A1A1A'),
    favicon: '🔷',
    colors: {
      primary: '#1565C0',
      primaryHover: '#10519A',
      onPrimary: '#FFFFFF',
      accent: '#26A69A',
    },
    supportEmail: 'reward@sentinel-defence.co.uk',
    supportPhone: '+44 20 7946 0001',
    headquartersCity: 'London',
  },
};

// ─── Subdomain → tenant ID lookup ────────────────────────────────────────
export const SUBDOMAIN_TO_TENANT = Object.fromEntries(
  Object.entries(TENANT_BRANDING).map(([tenantId, brand]) => [
    brand.subdomain,
    tenantId,
  ])
);

// ─── ClearEqual fallback (when no tenant subdomain matches) ──────────────
export const CLEAREQUAL_BRANDING = {
  subdomain: null,
  displayName: 'ClearEqual',
  legalName: 'ClearEqual Ltd.',
  logoUrl: null, // uses the built-in <Logo /> component
  logoMonoUrl: null,
  favicon: '⚖️',
  colors: {
    primary: '#0F172A',         // navy (matches deck)
    primaryHover: '#1E293B',
    onPrimary: '#FFFFFF',
    accent: '#F4A43A',          // gold (matches deck)
  },
  supportEmail: 'support@clearequal.com',
  supportPhone: null,
  headquartersCity: 'London',
};

export const getBranding = (tenantId) => {
  const base = TENANT_BRANDING[tenantId] || CLEAREQUAL_BRANDING;

  // Apply runtime admin overrides from tenantConfigStore. Done with a lazy
  // require to avoid a circular import (tenantConfigStore imports nothing
  // from this file, but the bundler is happier with a direct read here).
  try {
    const raw = typeof window !== 'undefined'
      ? localStorage.getItem(`clearequal.tenantConfig.${tenantId}`)
      : null;
    if (raw) {
      const cfg = JSON.parse(raw);
      const b = cfg?.branding || {};
      return {
        ...base,
        ...(b.displayName ? { displayName: b.displayName } : {}),
        ...(b.logoDataUrl ? { logoUrl: b.logoDataUrl } : {}),
        ...(b.colors ? { colors: { ...base.colors, ...b.colors } } : {}),
      };
    }
  } catch (_) { /* fall through to base */ }

  return base;
};
